// src/pages/dashboard/PractitionerDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import DashboardTour from '../../components/dashboard/DashboardTour';
import PracticeTools from '../../components/dashboard/PracticeTools';
import firestoreService from '../../services/firestore-service';
import { subscribeToConversations, subscribeToUnreadCount } from '../../services/messaging-service';
import { getPractitionerDisplayName } from '../../utils/getPractitionerDisplayName';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
// ---------------------------------------------------------------------------
// Past Due Payment Banner (module scope)
// ---------------------------------------------------------------------------

function PastDueBanner({ tierName, autoDowngradeAt, navigate }) {
  const formatDowngradeDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    } catch { return null; }
  };
  const downgradeDate = formatDowngradeDate(autoDowngradeAt);

  return (
    <div
      style={{
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '16px',
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '20px', flexShrink: 0, lineHeight: 1 }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '14px', fontWeight: 700, color: '#92400e', margin: '0 0 4px 0' }}>
            Your payment needs attention.
          </p>
          <p style={{ fontSize: '13px', color: '#92400e', margin: '0 0 12px 0', lineHeight: 1.5 }}>
            Update your payment method to keep your {tierName} plan.
            {downgradeDate && (
              <> Your account will be moved to the free tier on <strong>{downgradeDate}</strong>.</>
            )}
          </p>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard/subscription')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/subscription')}
            style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: '#d97706',
              color: 'white',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            Update payment method →
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verification Review Banners (module scope)
// ---------------------------------------------------------------------------

function PendingReviewBanner() {
  return (
    <div
      style={{
        background: '#f5f3ff',
        border: '1px solid #ddd6fe',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '16px',
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '20px', flexShrink: 0, lineHeight: 1 }}>🔍</span>
        <div>
          <p style={{ fontSize: '14px', fontWeight: 700, color: '#5b21b6', margin: '0 0 4px 0' }}>
            Your profile is under review
          </p>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: 0, lineHeight: 1.5 }}>
            We're reviewing your profile — this usually takes less than 24 hours. You'll receive an email once you're approved and live in the directory.
          </p>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: '8px 0 0 0', lineHeight: 1.5 }}>
            In the meantime, you can continue setting up your website, services, and availability.
          </p>
        </div>
      </div>
    </div>
  );
}

function RejectedReviewBanner({ feedback, navigate }) {
  return (
    <div
      style={{
        background: '#FEF2F2',
        border: '1px solid #FECACA',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '16px',
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '20px', flexShrink: 0, lineHeight: 1 }}>📝</span>
        <div>
          <p style={{ fontSize: '14px', fontWeight: 700, color: '#991B1B', margin: '0 0 4px 0' }}>
            Your profile needs a few updates
          </p>
          {feedback && (
            <div style={{
              padding: '10px 14px', background: 'white',
              border: '1px solid #FECACA', borderRadius: '8px',
              marginBottom: '10px', marginTop: '6px',
            }}>
              <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>
                "{feedback}"
              </p>
            </div>
          )}
          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 12px 0', lineHeight: 1.5 }}>
            Once you've made the updates, your profile will be automatically resubmitted for review.
          </p>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard/settings/profile')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/dashboard/settings/profile')}
            style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: '#7c3aed',
              color: 'white',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            Update my profile →
          </span>
        </div>
      </div>
    </div>
  );
}

function WidgetSetupBanner({ onDismiss, onSetup }) {
  return (
    <div style={{
      background: '#faf5eb', border: '1px solid #e8d9b8', borderRadius: '12px',
      padding: '16px 20px', marginBottom: '16px', fontFamily: "'Montserrat', sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ fontSize: '20px', flexShrink: 0, lineHeight: 1 }}>🔗</span>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '14px', fontWeight: 700, color: '#92400e', margin: '0 0 4px 0' }}>
            Connect your website
          </p>
          <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 12px 0', lineHeight: 1.5 }}>
            Add a booking button to your existing website so clients can book sessions directly.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <span role="button" tabIndex={0} onClick={onSetup}
              onKeyDown={(e) => e.key === 'Enter' && onSetup()}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                background: '#bb9749', color: '#fff', cursor: 'pointer', userSelect: 'none',
              }}>Set up booking widget</span>
            <span role="button" tabIndex={0} onClick={onDismiss}
              onKeyDown={(e) => e.key === 'Enter' && onDismiss()}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                background: 'transparent', color: '#6B7280', cursor: 'pointer', userSelect: 'none',
                border: '1px solid #D1D5DB',
              }}>Dismiss</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Practice Insights Widget (module scope — never re-mounts on render)
// ---------------------------------------------------------------------------

const INSIGHT_BORDER_COLORS = {
  booking:     '#3B82F6',
  revenue:     '#10B981',
  retention:   '#F59E0B',
  schedule:    '#7C3AED',
  review:      '#D4AF5A',
  celebration: '#EC4899',
};

// Teaser data shown to non-Premier users (blurred preview)
const TEASER_INSIGHTS = [
  { type: 'booking',     icon: '📈', title: 'Booking trend detected', body: 'Your Wednesday slots consistently fill first. Consider adding more availability mid-week.', action: 'Review your calendar availability' },
  { type: 'retention',   icon: '👤', title: '2 clients may need outreach', body: 'Two regulars haven\'t booked in 3+ weeks. A quick check-in often brings them back.', action: 'Send a follow-up message' },
  { type: 'celebration', icon: '🎉', title: 'Strong month so far', body: 'You\'re on track for your best revenue month yet. Your clients are responding well.', action: null },
];

function InsightCard({ insight, blurred }) {
  const borderColor = INSIGHT_BORDER_COLORS[insight.type] || '#7C3AED';
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: '10px',
        borderLeft: `3px solid ${borderColor}`,
        padding: '14px 16px',
        filter: blurred ? 'blur(4px)' : 'none',
        userSelect: blurred ? 'none' : 'auto',
        transition: 'box-shadow 0.15s ease',
        flex: '1 1 200px',
        minWidth: 0,
      }}
      onMouseEnter={(e) => { if (!blurred) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <p style={{ fontSize: '13px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>
        {insight.icon} {insight.title}
      </p>
      <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.5', margin: 0 }}>
        {insight.body}
      </p>
      {insight.action && !blurred && (
        <p style={{ fontSize: '12px', fontWeight: 500, color: '#7C3AED', marginTop: '8px', marginBottom: 0 }}>
          → {insight.action}
        </p>
      )}
    </div>
  );
}

function PracticeInsightsWidget({ insights, insightsLoading, isPremier, navigate }) {
  if (insightsLoading) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px', animation: 'pulse 2s infinite' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ flex: 1, height: '80px', background: '#F3F4F6', borderRadius: '8px' }} />
          ))}
        </div>
      </div>
    );
  }

  if (!isPremier) {
    // Teaser for non-Premier users
    return (
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px 24px', marginBottom: '24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: 0 }}>✨ Practice Insights</p>
        </div>
        {/* Blurred cards */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {TEASER_INSIGHTS.map((ins, i) => <InsightCard key={i} insight={ins} blurred />)}
        </div>
        {/* Overlay upgrade prompt */}
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.85)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          borderRadius: '12px', padding: '24px', textAlign: 'center',
        }}>
          <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>✨ Practice Coach Insights</p>
          <p style={{ fontSize: '13px', color: '#6B7280', maxWidth: '340px', marginBottom: '16px', lineHeight: 1.55 }}>
            AI-powered analysis of your bookings, revenue, and client retention. Available on the Premier plan.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            style={{ padding: '9px 22px', background: '#7C3AED', color: '#fff', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#6D28D9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#7C3AED'; }}
          >
            Upgrade →
          </button>
        </div>
      </div>
    );
  }

  // Empty state: Premier user with no insights yet
  if (!insights) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px 24px', marginBottom: '24px' }}>
        <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>✨ Practice Insights</p>
        <p style={{ fontSize: '13px', color: '#9CA3AF', margin: 0 }}>
          ✨ Practice insights will appear here once you have booking activity. Check back after your first week!
        </p>
      </div>
    );
  }

  // Format "Week of" date
  let weekLabel = '';
  if (insights.weekOf) {
    try {
      const d = new Date(insights.weekOf + 'T12:00:00Z');
      weekLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    } catch { weekLabel = insights.weekOf; }
  }

  const displayInsights = (insights.insights || []).slice(0, 3);

  return (
    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px 24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: 0 }}>✨ Practice Insights</p>
        {weekLabel && <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>Week of {weekLabel}</p>}
      </div>

      {/* Insight cards */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {displayInsights.map((ins, i) => <InsightCard key={i} insight={ins} blurred={false} />)}
      </div>

      {/* Top priority */}
      {insights.topPriority && (
        <div style={{ background: '#F5F3FF', borderRadius: '8px', padding: '10px 14px' }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: '#7C3AED', margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            🎯 Top Priority
          </p>
          <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: 1.5 }}>
            {insights.topPriority}
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inspiring messages that rotate daily
const inspiringMessages = [
  "Your gifts create ripples you'll never fully see",
  "Every client interaction is a chance for transformation",
  "You don't need to be perfect to make a difference",
  "Your practice is a living thing - let it grow at its own pace",
  "Trust the timing of your journey - clients find you when they need you",
  "Your energy is your currency - honor your boundaries",
  "The work you do matters more than you know",
  "Each session is a sacred exchange of energy and wisdom"
];

// Upgrade Banner Component (Free tier only)
const UpgradeBanner = ({ navigate }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900 mb-1">Ready to go live?</h3>
        <p className="text-gray-600 text-sm">
          Your practice is taking shape. When you're ready to start accepting bookings — with a DF website or your own — explore our plans.
        </p>
      </div>
      <button
        onClick={() => navigate('/pricing')}
        className="px-5 py-2 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-700 transition-colors whitespace-nowrap"
      >
        View Plans →
      </button>
    </div>
  </div>
);

// AI Session Prep Card (locked preview for Free/Pro, active for Elite)
const AISessionPrepCard = ({ tier }) => {
  if (tier !== 'free' && tier !== 'pro') return null;

  return (
    <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl shadow-sm border border-purple-200 p-6 mb-6 relative opacity-75">
      <div className="absolute top-4 right-4">
        <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
          Elite
        </span>
      </div>

      <div className="flex items-start gap-4">
        <div className="bg-white/50 rounded-full p-3 flex-shrink-0">
          <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>

        <div className="flex-1">
          <h3 className="font-bold text-lg text-gray-900 mb-2">AI Session Prep</h3>
          <p className="text-gray-700 text-sm mb-3">
            Get AI-powered prep notes before every session
          </p>
          <p className="text-gray-600 text-xs">
            Upgrade to Elite to unlock AI features that help you serve clients better.
          </p>
        </div>
      </div>
    </div>
  );
};

const PractitionerDashboard = () => {
  const navigate = useNavigate();

  // Tier detection - load from localStorage for dev testing
  const [userTier, setUserTier] = useState(() => {
    // Check dev override first
    const devTier = localStorage.getItem('dev_subscription_tier');
    if (devTier) return devTier;

    // Default to free tier
    return 'free';
  });

  // Listen for admin tier-switch changes
  useEffect(() => {
    const handleStorage = () => {
      const devTier = localStorage.getItem('dev_subscription_tier');
      const tier = devTier || localStorage.getItem('userTier') || 'free';
      setUserTier(tier);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Practice Coach insights (loaded from Firestore)
  const [practiceInsights, setPracticeInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);

  // Dunning / past-due state (loaded alongside practiceInsights)
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [autoDowngradeAt, setAutoDowngradeAt] = useState(null);

  // Verification / review state
  const [reviewStatus, setReviewStatus] = useState(null); // 'pending' | 'approved' | 'rejected' | null
  const [rejectionFeedback, setRejectionFeedback] = useState(null);
  // Widget setup prompt (for existing-website practitioners)
  const [showWidgetPrompt, setShowWidgetPrompt] = useState(false);

  // Elite banner dismissal logic
  const [showEliteBanner, setShowEliteBanner] = useState(true);

  const [practitionerName, setPractitionerName] = useState('');

  // Restore onboarding flags from Firestore on mount (new device / cleared storage
  // recovery) and resolve the practitioner's first name for the welcome banner.
  useEffect(() => {
    const loadProgress = async () => {
      try {
        // Firestore fallback: if onboarding_completed flag missing locally, check Firestore
        const onboardingComplete = localStorage.getItem('onboarding_completed') === 'true';
        if (!onboardingComplete) {
          const userId = firestoreService.getCurrentUserId();
          if (userId) {
            try {
              const firestoreData = await firestoreService.getOnboardingData(userId);
              if (firestoreData?.completed) {
                localStorage.setItem('onboarding_completed', 'true');
                if (firestoreData.data) {
                  localStorage.setItem('onboarding_data', JSON.stringify(firestoreData.data));
                }
              }
            } catch (err) {
              console.warn('Firestore onboarding check failed:', err);
            }
          }
        }

        // Resolve practitioner's first name from all available sources.
        // getPractitionerDisplayName checks: practitioner_setup, practitioner_profile,
        // publicProfile localStorage keys, and Firebase Auth currentUser.displayName.
        const resolvedName = getPractitionerDisplayName(null)
          || JSON.parse(localStorage.getItem('onboarding_data') || '{}').displayName
          || '';
        // Use only the first name for a warmer greeting ("Welcome back, Patricia!")
        setPractitionerName(resolvedName ? resolvedName.trim().split(' ')[0] : '');
      } catch (error) {
        console.error('Error loading progress:', error);
      }
    };

    loadProgress();
  }, []);

  // Load practice insights from Firestore
  useEffect(() => {
    if (!auth || !db) { setInsightsLoading(false); return; }
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setInsightsLoading(false); } }, 4000);
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && !done) {
        done = true;
        clearTimeout(timeout);
        try {
          const snap = await getDoc(doc(db, 'practitioners', user.uid));
          if (snap.exists()) {
            const data = snap.data();
            setPracticeInsights(data.practiceInsights || null);
            // Dunning state for past-due banner
            setPaymentStatus(data.subscription?.paymentStatus || null);
            setAutoDowngradeAt(data.subscription?.autoDowngradeAt || null);
            // Verification / review state
            setReviewStatus(data.verification?.manualReviewStatus || null);
            setRejectionFeedback(data.verification?.rejectionFeedback || null);
            // Quick Stats data sources
            setSiteViews(data.siteViews || {});
            setReviewStats(data.reviewStats || null);
            // Widget prompt for existing-website practitioners
            if (data.hasExistingWebsite && !data.widgetPromptDismissed) {
              setShowWidgetPrompt(true);
            }
          }
        } catch (err) {
          console.warn('PractitionerDashboard: failed to load practiceInsights:', err.message);
        }
        setInsightsLoading(false);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // Real bookings data (replaces mock todaySessions array)
  const [todayBookings, setTodayBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [monthEarnings, setMonthEarnings] = useState(0);

  // Real messaging data (replaces mock recentNotifications + unreadCount)
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Profile views + review stats from practitioner doc
  const [siteViews, setSiteViews] = useState({});
  const [reviewStats, setReviewStats] = useState(null);

  // Load practitioner's bookings for Today's Schedule + Quick Stats + Next Session.
  // Mirrors Analytics.js pattern (no orderBy, sort client-side) to avoid the
  // composite-index requirement.
  useEffect(() => {
    if (!auth || !db) { setBookingsLoading(false); return; }
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setBookingsLoading(false); } }, 4000);
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && !done) {
        done = true;
        clearTimeout(timeout);
        try {
          const q = query(collection(db, 'bookings'), where('practitionerId', '==', user.uid));
          const snap = await getDocs(q);
          const allBookings = snap.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              dateTime: data.dateTime?.toDate?.() || new Date(data.dateTime),
            };
          });
          allBookings.sort((a, b) => a.dateTime - b.dateTime);

          const now = new Date();
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const tomorrowStart = new Date(todayStart);
          tomorrowStart.setDate(tomorrowStart.getDate() + 1);

          const today = allBookings.filter(b => b.dateTime >= todayStart && b.dateTime < tomorrowStart);
          const upcoming = allBookings.filter(b => b.dateTime > now);

          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          const monthly = allBookings.filter(b => b.dateTime >= monthStart && b.dateTime < monthEnd);
          const earnings = monthly.reduce((sum, b) => sum + (b.service?.price || 0), 0);

          setTodayBookings(today);
          setUpcomingBookings(upcoming);
          setMonthEarnings(earnings);
        } catch (err) {
          console.warn('PractitionerDashboard: failed to load bookings:', err.message);
        }
        setBookingsLoading(false);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // Subscribe to messaging-service: real conversations + unread count
  useEffect(() => {
    if (!auth) return;
    let unsubConvos = null;
    let unsubUnread = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        unsubConvos = subscribeToConversations(user.uid, (convos) => {
          setConversations(convos || []);
        });
        unsubUnread = subscribeToUnreadCount(user.uid, (count) => {
          setUnreadCount(count || 0);
        });
      }
    });
    return () => {
      unsubAuth?.();
      unsubConvos?.();
      unsubUnread?.();
    };
  }, []);

  // Tour state management
  const [runTour, setRunTour] = useState(false);

  // Check if tour should run on mount
  useEffect(() => {
    const tourRequested = localStorage.getItem('tour_requested') === 'true';
    if (tourRequested) {
      // Small delay to ensure all elements are rendered
      setTimeout(() => {
        setRunTour(true);
      }, 500);
    }
  }, []);

  const handleTourComplete = () => {
    setRunTour(false);
    localStorage.removeItem('tour_requested');
  };

  // Rotate inspiring message daily based on date
  const messageIndex = new Date().getDate() % inspiringMessages.length;
  const inspiringMessage = inspiringMessages[messageIndex];

  // Elite banner dismissal check
  useEffect(() => {
    if (userTier === 'pro') {
      const dismissalData = localStorage.getItem('eliteUpgradeDismissed');
      if (dismissalData) {
        const { timestamp, count } = JSON.parse(dismissalData);
        const daysSinceDismissal = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);

        // If dismissed more than 5 times, stop showing (they're not interested)
        if (count >= 5) {
          setShowEliteBanner(false);
          return;
        }

        // If less than 14 days since last dismissal, don't show
        if (daysSinceDismissal < 14) {
          setShowEliteBanner(false);
        }
      }
    }
  }, [userTier]);

  const handleDismissEliteBanner = () => {
    const existingData = localStorage.getItem('eliteUpgradeDismissed');
    const count = existingData ? JSON.parse(existingData).count + 1 : 1;

    localStorage.setItem('eliteUpgradeDismissed', JSON.stringify({
      timestamp: Date.now(),
      count: count
    }));

    setShowEliteBanner(false);
  };

  // Derive next upcoming session from real bookings data
  const nextSession = upcomingBookings[0] || null;

  // Format a booking's client name with a graceful fallback
  const getBookingClientName = (b) => {
    if (!b) return 'your client';
    const first = b.client?.firstName?.trim();
    const last = b.client?.lastName?.trim();
    const joined = [first, last].filter(Boolean).join(' ');
    return joined || b.client?.email || 'your client';
  };

  // Format a booking's time as "2:00 PM"
  const formatBookingTime = (b) => {
    if (!b?.dateTime) return '';
    return b.dateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Compute "in X hours" / "in X min" / "Tomorrow at Y" relative-time string
  const formatTimeUntil = (b) => {
    if (!b?.dateTime) return '';
    const now = new Date();
    const diffMs = b.dateTime - now;
    if (diffMs <= 0) return 'starting now';
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 60) return `${diffMin} min`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return diffHr === 1 ? '1 hour' : `${diffHr} hours`;
    const diffDay = Math.round(diffHr / 24);
    return diffDay === 1 ? '1 day' : `${diffDay} days`;
  };

  // Relative timestamp ("just now", "5 min ago", "2 hours ago", "Yesterday", "Mar 5")
  const formatRelativeTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 60000) return 'just now';
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return diffHr === 1 ? '1 hour ago' : `${diffHr} hours ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay === 1) return 'Yesterday';
    if (diffDay < 7) return `${diffDay} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Booking-row date label ("Today" / "Tomorrow" / "Mon, May 18")
  const formatBookingDateLabel = (b) => {
    if (!b?.dateTime) return '';
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const bDay = new Date(b.dateTime.getFullYear(), b.dateTime.getMonth(), b.dateTime.getDate());
    if (bDay.getTime() === today.getTime()) return 'Today';
    if (bDay.getTime() === tomorrow.getTime()) return 'Tomorrow';
    return b.dateTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-8">
      {/* Old DEV MODE tier selector removed - use AdminTierOverride instead (bottom-right pill) */}

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Unified dashboard for all tiers - tier-aware rendering */}

        {/* For old wizard users (Free tier only) who haven't completed - show old interface */}
        {/* Dashboard is always accessible - setup is optional */}
        {false ? (
          <div className="text-center py-12">
            {/* This section has been disabled - dashboard is always accessible */}
          </div>
        ) : (
          <>
        {/* Welcome Section with Rotating Message */}
        <div
          className="rounded-xl p-6 mb-6"
          style={{
            background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
            backgroundSize: '400% 400%',
            animation: 'auroraShift 14s ease infinite',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1" style={{ color: '#FFFFFF' }}>
                {practitionerName ? `Welcome back, ${practitionerName}! 🌙` : 'Welcome back! 🌙'}
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.8)' }}>
                "{inspiringMessage}"
              </p>
            </div>
          </div>
        </div>

        {/* ── Past Due Payment Banner ── */}
        {paymentStatus === 'past_due' && (
          <PastDueBanner
            tierName={{ free: 'Essentials', pro: 'Professional', elite: 'Premier' }[userTier] || userTier}
            autoDowngradeAt={autoDowngradeAt}
            navigate={navigate}
          />
        )}

        {/* ── Verification Review Banners ── */}
        {reviewStatus === 'pending' && <PendingReviewBanner />}
        {reviewStatus === 'rejected' && (
          <RejectedReviewBanner feedback={rejectionFeedback} navigate={navigate} />
        )}

        {/* ── Widget Setup Prompt ── */}
        {showWidgetPrompt && (
          <WidgetSetupBanner
            onSetup={() => navigate('/dashboard/settings/widget')}
            onDismiss={async () => {
              setShowWidgetPrompt(false);
              try {
                const uid = auth?.currentUser?.uid;
                if (uid) await updateDoc(doc(db, 'practitioners', uid), { widgetPromptDismissed: true });
              } catch { /* ignore */ }
            }}
          />
        )}

        {/* ── Practice Coach Insights Widget ── */}
        <PracticeInsightsWidget
          insights={practiceInsights}
          insightsLoading={insightsLoading}
          isPremier={userTier === 'elite' || userTier === 'premier'}
          navigate={navigate}
        />

        {/* AI Session Prep - Elite (active), Free/Pro (locked preview) */}
        {userTier === 'free' || userTier === 'pro' ? (
          <AISessionPrepCard tier={userTier} />
        ) : userTier === 'elite' && nextSession && (
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl shadow-lg p-6 mb-6 relative overflow-hidden">
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none" />

            <div className="relative flex items-start gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-bold text-xl text-white">AI Session Prep</h3>
                  <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                    Elite
                  </span>
                </div>

                <p className="text-purple-100 text-sm mb-4 font-medium">
                  Your next session with {getBookingClientName(nextSession)} is in {formatTimeUntil(nextSession)}
                </p>

                <button
                  onClick={() => navigate(nextSession.clientId ? `/dashboard/prep/${nextSession.clientId}` : '/dashboard/clients')}
                  className="px-5 py-2.5 bg-white text-purple-700 rounded-lg text-sm font-bold hover:bg-purple-50 transition-all shadow-md hover:shadow-lg"
                >
                  View Full Prep Notes →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hero Section: Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-tour="schedule-messages">

          {/* Left Column: Today's Schedule */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Today's Schedule</h2>
                <p className="text-sm text-gray-500">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Content area - grows to fill space */}
            <div className="flex-1">
              {userTier === 'free' ? (
                /* Free tier - warm empty state */
                <div className="text-center py-8">
                  <p className="text-4xl mb-3">📅</p>
                  <p className="text-gray-600 mb-2">Your first booking will show up here</p>
                  <p className="text-sm text-gray-500">
                    Upgrade to Pro to start accepting client bookings
                  </p>
                </div>
              ) : todayBookings.length > 0 ? (
                <>
                  {/* Next Up Session - first today's booking */}
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">⏰</span>
                        <div>
                          <p className="text-sm font-medium text-purple-600">Next Up</p>
                          <p className="text-lg font-bold text-gray-900">{formatBookingTime(todayBookings[0])}</p>
                          <p className="text-sm text-gray-500">in {formatTimeUntil(todayBookings[0])}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <h3 className="font-semibold text-gray-900">{todayBookings[0].service?.name || 'Session'}</h3>
                      <p className="text-sm text-gray-600">with {getBookingClientName(todayBookings[0])}</p>
                    </div>

                    <div className="mt-4">
                      <button
                        onClick={() => navigate(todayBookings[0].clientId ? `/dashboard/clients/${todayBookings[0].clientId}` : '/dashboard/clients')}
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                      >
                        View Client
                      </button>
                    </div>
                  </div>

                  {/* Upcoming Sessions */}
                  <div className="space-y-3">
                    {todayBookings.slice(1, 3).map(session => (
                      <div
                        key={session.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:bg-purple-50 transition-all cursor-pointer"
                        onClick={() => navigate(session.clientId ? `/dashboard/clients/${session.clientId}` : '/dashboard/clients')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">📅</span>
                            <div>
                              <p className="font-medium text-gray-900">{session.service?.name || 'Session'}</p>
                              <p className="text-sm text-gray-600">with {getBookingClientName(session)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{formatBookingTime(session)}</p>
                            <p className="text-xs text-gray-500">{formatBookingDateLabel(session)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-4xl mb-3">📅</p>
                  <p className="text-gray-600 mb-4">No sessions today</p>
                  <button
                    onClick={() => navigate('/dashboard/calendar')}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                  >
                    Manage Availability
                  </button>
                </div>
              )}
            </div>

            {/* View Full Calendar button - pinned to bottom */}
            {todayBookings.length > 0 && (
              <button
                onClick={() => navigate('/dashboard/calendar')}
                className="w-full mt-4 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                View Full Calendar
                <span>→</span>
              </button>
            )}
          </div>

          {/* Right Column: Messages & Notifications */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Messages & Notifications</h2>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>

            {/* Content area - grows to fill space */}
            <div className="flex-1">
              {userTier === 'free' ? (
                /* Free tier - warm empty state */
                <div className="text-center py-8">
                  <p className="text-4xl mb-3">💬</p>
                  <p className="text-gray-600 mb-2">Messages from clients will appear here</p>
                  <p className="text-sm text-gray-500">
                    Upgrade to Pro to enable client messaging
                  </p>
                </div>
              ) : conversations.length > 0 ? (
                <div className="space-y-4">
                  {conversations.slice(0, 3).map(conv => (
                    <div
                      key={conv.id}
                      onClick={() => navigate(`/messages/${conv.id}`)}
                      className={`border rounded-lg p-4 transition-all cursor-pointer hover:shadow-sm ${
                        conv.unreadCount > 0 ? 'bg-blue-50 border-blue-200' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">💬</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-1">
                            <p className="font-medium text-gray-900 truncate">{conv.clientName || 'New message'}</p>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-2">{formatRelativeTime(conv.lastMessageAt)}</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1 truncate">{conv.lastMessagePreview || conv.subject}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-4xl mb-3">💬</p>
                  <p className="text-gray-600 mb-2">No new messages</p>
                </div>
              )}
            </div>

            {/* View All Messages button - pinned to bottom */}
            <button
              onClick={() => navigate('/messages')}
              className="w-full mt-4 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              View All Messages
              <span>→</span>
            </button>
          </div>
        </div>

        {/* Stat Cards (4-column grid) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Upcoming Sessions */}
          <div
            onClick={() => navigate('/dashboard/calendar')}
            className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="bg-blue-100 p-3 rounded-full">
                <span className="text-2xl">📅</span>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">
              {upcomingBookings.length}
            </h3>
            <p className="text-sm text-gray-600">Upcoming Sessions</p>
          </div>

          {/* This Month earnings */}
          <div
            onClick={() => navigate('/dashboard/earnings')}
            className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="bg-green-100 p-3 rounded-full">
                <span className="text-2xl">💰</span>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">
              ${monthEarnings.toLocaleString()}
            </h3>
            <p className="text-sm text-gray-600">This Month</p>
          </div>

          {/* Profile Views (this month) */}
          <div
            onClick={() => navigate('/dashboard/analytics')}
            className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="bg-purple-100 p-3 rounded-full">
                <span className="text-2xl">👁️</span>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">
              {siteViews[new Date().toISOString().slice(0, 7)] || 0}
            </h3>
            <p className="text-sm text-gray-600">Profile Views</p>
          </div>

          {/* Average Rating */}
          <div
            onClick={() => navigate('/dashboard/reviews')}
            className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="bg-yellow-100 p-3 rounded-full">
                <span className="text-2xl">⭐</span>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-1">
              {reviewStats?.averageRating ? reviewStats.averageRating.toFixed(1) : '0.0'}
            </h3>
            <p className="text-sm text-gray-600">Average Rating</p>
          </div>
        </div>

        {/* Soft Upgrade Banner - Free tier only, between stats and Practice Tools */}
        {userTier === 'free' && <UpgradeBanner navigate={navigate} />}

        {/* Unlock Elite Features - Pro Tier Only */}
        {userTier === 'pro' && (
          <div className="mb-6">
            {showEliteBanner ? (
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 text-8xl opacity-10">✨</div>

              {/* Dismiss Button */}
              <button
                onClick={handleDismissEliteBanner}
                className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 pr-8">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                        Elite Features
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Unlock AI-Powered Practice Growth
                    </h2>
                    <p className="text-purple-100 text-sm">
                      Upgrade to Elite for AI insights that help you work smarter, grow faster, and serve clients better
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* AI Session Prep */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="text-3xl mb-2">🧠</div>
                    <h3 className="font-semibold text-white mb-1">AI Session Prep</h3>
                    <p className="text-purple-100 text-xs">
                      Get AI-synthesized prep notes before every session
                    </p>
                  </div>

                  {/* AI Practice Coach */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="text-3xl mb-2">⚡</div>
                    <h3 className="font-semibold text-white mb-1">AI Practice Coach</h3>
                    <p className="text-purple-100 text-xs">
                      Weekly insights on bookings, content, and growth opportunities
                    </p>
                  </div>

                  {/* Zoom AI Summaries */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="mb-2">
                      <svg className="w-8 h-8" viewBox="0 0 48 48" fill="none">
                        <rect width="48" height="48" rx="8" fill="#2D8CFF"/>
                        <path d="M12 17.5C12 15.567 13.567 14 15.5 14H26.5C28.433 14 30 15.567 30 17.5V30.5C30 32.433 28.433 34 26.5 34H15.5C13.567 34 12 32.433 12 30.5V17.5Z" fill="white"/>
                        <path d="M30 21L36 17V31L30 27V21Z" fill="white"/>
                      </svg>
                    </div>
                    <h3 className="font-semibold text-white mb-1">Zoom AI Summaries</h3>
                    <p className="text-purple-100 text-xs">
                      AI-generated session summaries auto-saved to client notes
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/pricing')}
                  className="px-6 py-3 bg-yellow-400 text-gray-900 rounded-lg font-bold text-sm hover:bg-yellow-300 transition-all shadow-lg"
                >
                  Upgrade to Elite →
                </button>
              </div>
            </div>
            ) : (
              /* Small replacement card after dismissal */
              <div className="bg-white rounded-lg shadow-sm border border-purple-200 p-4 hover:border-purple-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">✨</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        Interested in AI-powered features?
                      </p>
                      <p className="text-xs text-gray-600">
                        Learn about Elite tier benefits
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="px-4 py-2 text-purple-600 text-sm font-semibold hover:text-purple-700 transition-colors flex items-center gap-1"
                  >
                    Learn More
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Practice Tools */}
        <PracticeTools />

        {/* Help Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-600 text-sm mb-4">Explore resources to get the most out of your practice</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => window.open('/help', '_blank')}
              className="px-5 py-2 bg-purple-600 text-white rounded-lg font-semibold text-sm hover:bg-purple-700 transition-colors"
            >
              View Help Docs
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </button>
          </div>
        </div>
          </>
        )}
      </div>

      {/* Mobile Bottom Nav - Only show on small screens */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="grid grid-cols-5 gap-1">
          {[
            { icon: '🏠', label: 'Home', path: '/dashboard' },
            { icon: '📅', label: 'Calendar', path: '/dashboard/calendar' },
            { icon: '💬', label: 'Messages', path: '/messages', badge: unreadCount },
            { icon: '👤', label: 'Clients', path: '/dashboard/clients' },
            { icon: '⚙️', label: 'More', path: '/dashboard/settings/account' }
          ].map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="relative flex flex-col items-center justify-center py-3 hover:bg-gray-50"
            >
              <span className="text-xl">{item.icon}</span>
              <span className="text-xs text-gray-600 mt-1">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute top-1 right-1/4 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Dashboard Tour */}
      <DashboardTour run={runTour} onComplete={handleTourComplete} />
    </div>
  );
};

export default PractitionerDashboard;
